import { Button, Field } from '@catering/ui';
import { Plus, Trash2 } from 'lucide-react';
import type { JSX } from 'react';
import {
  useFieldArray,
  type Control,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';

import {
  DEFAULT_WITHHOLDING_LINE,
  pesosToCents,
  type PaymentFormValues,
} from '../../../payments/payment-form-schema';
import { suggestWithholdingCents } from '../../../payments/suggest-withholding';
import type { WithholdingTypeOption } from '../../../payments/withholding-types-api';

export interface WithholdingLineEditorProps {
  control: Control<PaymentFormValues>;
  register: UseFormRegister<PaymentFormValues>;
  getValues: UseFormGetValues<PaymentFormValues>;
  setValue: UseFormSetValue<PaymentFormValues>;
  /** Active withholding types (`GET /withholding-types/options`). */
  typeOptions: WithholdingTypeOption[];
  errors: FieldErrors<PaymentFormValues>['withholdings'];
}

type WithholdingLineFieldError = NonNullable<
  FieldErrors<PaymentFormValues>['withholdings']
>[number];

/**
 * `payment-receipts/spec.md` → "Retenciones y percepciones como líneas del
 * recibo". Same `useFieldArray` pattern as `OrderItemEditor`. `jurisdiction`
 * has no `required` marker: it "solo aplica a IIBB" per the spec, never a
 * hard requirement of the line itself. Picking a type prefills the
 * jurisdiction and, only while the line amount is empty and a received amount
 * exists, the amount from the type's default rate; both stay editable.
 */
export function WithholdingLineEditor({
  control,
  register,
  getValues,
  setValue,
  typeOptions,
  errors,
}: WithholdingLineEditorProps): JSX.Element {
  const { fields, append, remove } = useFieldArray({ control, name: 'withholdings' });

  function applyTypeDefaults(index: number, typeId: string): void {
    const type = typeOptions.find((option) => option.id === typeId);

    setValue(`withholdings.${index}.jurisdiction`, type?.jurisdiction ?? '');

    if (type === undefined || getValues(`withholdings.${index}.amountPesos`).trim() !== '') return;

    const received = getValues('amountPesos');
    const receivedCents = pesosToCents(received);
    if (!Number.isFinite(receivedCents) || receivedCents <= 0) return;

    const suggested = suggestWithholdingCents(receivedCents, type.defaultRatePercent);
    if (suggested !== null) {
      setValue(`withholdings.${index}.amountPesos`, (suggested / 100).toFixed(2));
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {fields.length === 0 ? (
        <p className="text-sm text-slate-500">Sin líneas de retención.</p>
      ) : null}

      {fields.map((field, index) => {
        const lineError: WithholdingLineFieldError | undefined = Array.isArray(errors)
          ? (errors as WithholdingLineFieldError[])[index]
          : undefined;

        const typeField = register(`withholdings.${index}.typeId` as const);

        return (
          <div
            key={field.id}
            className="grid grid-cols-1 gap-3 sm:grid-cols-[1.2fr_1fr_1.2fr_1fr_auto] sm:items-end"
          >
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor={`withholding-${index}-type`}
                className="text-sm font-medium text-slate-900"
              >
                Tipo
              </label>
              <select
                id={`withholding-${index}-type`}
                className="h-11 rounded-md border border-slate-300 px-3 text-sm"
                {...typeField}
                onChange={(event) => {
                  void typeField.onChange(event);
                  applyTypeDefaults(index, event.target.value);
                }}
              >
                <option value="">Seleccioná un tipo</option>
                {typeOptions.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {lineError?.typeId?.message === undefined ? null : (
                <p role="alert" className="text-xs text-red-600">
                  {lineError.typeId.message}
                </p>
              )}
            </div>

            <Field
              label="Monto"
              inputMode="decimal"
              {...register(`withholdings.${index}.amountPesos` as const)}
              {...(lineError?.amountPesos?.message === undefined
                ? {}
                : { error: lineError.amountPesos.message })}
            />

            <Field
              label="Número de certificado"
              {...register(`withholdings.${index}.certificateNumber` as const)}
              {...(lineError?.certificateNumber?.message === undefined
                ? {}
                : { error: lineError.certificateNumber.message })}
            />

            <Field
              label="Jurisdicción (si corresponde)"
              {...register(`withholdings.${index}.jurisdiction` as const)}
            />

            <Button
              type="button"
              variant="secondary"
              icon={Trash2}
              aria-label="Quitar retención"
              onClick={() => remove(index)}
            >
              Quitar
            </Button>
          </div>
        );
      })}

      <div>
        <Button
          type="button"
          variant="secondary"
          icon={Plus}
          onClick={() => append(DEFAULT_WITHHOLDING_LINE)}
        >
          Agregar retención
        </Button>
      </div>
    </div>
  );
}
